/**
 * 빌드 때 프리렌더(scripts/prerender.ts)가 쓰는 순수 함수들.
 *
 * 색인 허용 경로(`INDEXABLE_PATHS`)만 HTML 을 미리 만든다. SPA 셸(`dist/index.html`)은 그대로
 * 두고 결과를 `dist/prerender/<경로>/index.html` 에 따로 쓴다 — 셸은 `/g/<token>`·콘솔에도
 * 서빙되므로 거기에 랜딩 마크업을 넣으면 게스트가 자기 안내 대신 랜딩을 먼저 보게 된다.
 * nginx 가 색인 허용 경로에만 프리렌더 파일을 준다(`deployments/apps/web/nginx.conf`).
 */
import type { IndexablePath } from "./seo";

/**
 * 경로 → 그 화면의 lazy 청크 소스. `App.tsx` 의 `import()` 경로와 같아야 한다
 * (Vite manifest 의 키가 이 경로다). `prerender-html.test.ts` 가 둘을 맞춰 본다.
 */
export const PRERENDER_ROUTES: Record<IndexablePath, string> = {
  "/": "src/pages/LandingPage.tsx",
  "/guide": "src/pages/UserGuidePage.tsx",
  "/faq": "src/pages/FaqPage.tsx",
  "/terms": "src/pages/TermsPage.tsx",
  "/privacy": "src/pages/PrivacyPage.tsx",
};

/** 없는 주소에 nginx 가 404 상태로 주는 파일(dist 기준). `error_page 404` 와 같아야 한다. */
export const NOT_FOUND_FILE = "prerender/404.html";

/** 404 화면의 <head> 값 — 셸 설명을 그대로 두면 검색 결과·미리보기에 랜딩 문구가 붙는다. */
export const NOT_FOUND_META = {
  title: "페이지를 찾을 수 없어요 · 스테이라인",
  description:
    "주소가 바뀌었거나 없는 페이지예요. 스테이라인 처음 화면이나 운영자 콘솔에서 다시 시작해 주세요.",
};

/** 경로 → 출력 파일(dist 기준). */
export const prerenderFile = (path: string): string =>
  path === "/" ? "prerender/index.html" : `prerender${path}/index.html`;

export function escapeAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** canonical 이 없으면(404) 넣지 않는다 — 없는 주소를 색인 대상으로 선언하지 않게. */
export type HeadMeta = { title: string; description: string; canonical?: string };

/**
 * 셸의 제목·설명·og/twitter 값을 페이지 값으로 바꾸고 canonical·og:url 을 넣는다.
 * 원래 셸 값은 `data-shell` 에 남긴다 — 앱 안에서 다른 경로로 나갈 때 `PublicLayout` 이
 * 그 값으로 되돌린다(직전 값으로 되돌리면 /admin 에 랜딩 제목이 남는다).
 */
export function applyHead(template: string, meta: HeadMeta): string {
  let html = template.replace(
    /<title>([^<]*)<\/title>/,
    (_, shell: string) =>
      `<title data-shell="${escapeAttr(shell)}">${escapeAttr(meta.title)}</title>`
  );
  const values: [string, string][] = [
    ['name="description"', meta.description],
    ['property="og:title"', meta.title],
    ['property="og:description"', meta.description],
    ['name="twitter:title"', meta.title],
    ['name="twitter:description"', meta.description],
  ];
  for (const [key, value] of values) {
    const pattern = new RegExp(`<meta\\s+${key}\\s+content="([^"]*)"`);
    if (!pattern.test(html)) throw new Error(`index.html 에 <meta ${key} content="…"> 가 없어요`);
    html = html.replace(
      pattern,
      (_, shell: string) => `<meta ${key} data-shell="${shell}" content="${escapeAttr(value)}"`
    );
  }
  if (!meta.canonical) return html;
  const canonical = escapeAttr(meta.canonical);
  return html.replace(
    "</head>",
    `  <link rel="canonical" href="${canonical}" />\n    <meta property="og:url" content="${canonical}" />\n  </head>`
  );
}

type ManifestChunk = { file: string; css?: string[]; imports?: string[] };
export type ViteManifest = Record<string, ManifestChunk>;

/**
 * 그 화면이 쓰는 청크와 CSS. 프리렌더 HTML 의 <head> 에 미리 걸어 두면 hydrate 할 청크를
 * HTML 을 읽는 동안 받기 시작하고, 랜딩 전용 CSS 가 없어 맨글자로 한 번 그려지는 일도 없다.
 * 셸이 이미 거는 파일(엔트리·vendor)은 뺀다.
 */
export function routeAssets(
  manifest: ViteManifest,
  source: string,
  template: string
): { js: string[]; css: string[] } {
  const js = new Set<string>();
  const css = new Set<string>();
  const seen = new Set<string>();
  const visit = (key: string) => {
    if (seen.has(key)) return;
    seen.add(key);
    const chunk = manifest[key];
    if (!chunk)
      throw new Error(
        `manifest 에 ${key} 가 없어요 — App.tsx 의 import() 경로와 맞는지 확인하세요`
      );
    js.add(chunk.file);
    for (const c of chunk.css ?? []) css.add(c);
    for (const dep of chunk.imports ?? []) visit(dep);
  };
  visit(source);
  const notInShell = (file: string) => !template.includes(`/${file}"`);
  return { js: [...js].filter(notInShell), css: [...css].filter(notInShell) };
}

/** 렌더한 앱을 #root 에 넣고, 그 화면의 청크·CSS 를 <head> 에 건다. */
export function injectApp(
  template: string,
  appHtml: string,
  assets: { js: string[]; css: string[] }
): string {
  if (!template.includes('<div id="root"></div>')) {
    throw new Error('index.html 에 빈 <div id="root"></div> 가 없어요');
  }
  const links = [
    ...assets.css.map((f) => `<link rel="stylesheet" crossorigin href="/${f}">`),
    ...assets.js.map((f) => `<link rel="modulepreload" crossorigin href="/${f}">`),
  ]
    .map((l) => `    ${l}\n`)
    .join("");
  return template
    .replace("</head>", `${links}  </head>`)
    .replace('<div id="root"></div>', () => `<div id="root">${appHtml}</div>`);
}
